// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Table component for displaying assets with date inconsistencies.
 *
 * Shows assets where dates are out of order or activity occurred before
 * the asset's inventory date. Uses react-window for virtualized rendering
 * to handle large datasets efficiently.
 */
import { EmptyState } from '@/components/empty-state';
import { Link } from '@/components/link';
import { TruncatedText } from '@/components/truncated-text';
import { ExceptionType } from '@/constants/asset-exception-type';
import { CheckCircleOutlined } from '@/constants/hardfin-standard-icons';
import { DateInconsistencyItem } from '@/report/api/types';
import { AssetExceptionResolutionDialog } from '@/report/components/asset-exception-resolution-dialog';
import { getExceptionConfig } from '@/report/utilities/exception-config';
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

// Height of each row in pixels for virtualization
const ROW_HEIGHT = 57;

// Number of rows to pre-render outside the visible area
const OVERSCAN_COUNT = 10;

// Maximum height for the virtualized table body
const MAX_TABLE_HEIGHT = 800;

interface Props {
  assets: DateInconsistencyItem[];
}

// Data passed to the virtualized row renderer
interface RowData {
  assets: DateInconsistencyItem[];
  onSelectAsset: (item: DateInconsistencyItem) => void;
  resolvedAssetIds: Set<string>;
}

// Virtualized row renderer for the date inconsistencies table
function VirtualizedRow(props: ListChildComponentProps<RowData>) {
  const item = props.data.assets[props.index];
  const isResolved = props.data.resolvedAssetIds.has(item.asset.id);

  return (
    <Box
      style={props.style}
      sx={{
        display: 'grid',
        gridTemplateColumns:
          'minmax(150px, 1.5fr) repeat(6, minmax(90px, 1fr)) minmax(120px, 0.5fr)',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 2,
        bgcolor: isResolved ? 'success.50' : 'transparent',
      }}
    >
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        <Link
          to="/assets/$assetKey"
          color="primary.main"
          variant="subtitle2"
          params={{ assetKey: item.asset.key }}
        >
          {item.asset.serial}
        </Link>
        <TruncatedText
          text={`${item.asset.item.sku} (${item.asset.item.name})`}
          variant="caption"
          color="text.secondary"
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <Typography variant="body2">
          {item.inventoryDate
            ? item.inventoryDate.toLocaleString({ dateStyle: 'medium' })
            : '—'}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <DateCellWithException
          date={item.inServiceDate}
          exceptionType={
            isResolved ||
            !item.exceptionTypes.includes('IN_SERVICE_DATE_BEFORE_INVENTORY')
              ? null
              : 'IN_SERVICE_DATE_BEFORE_INVENTORY'
          }
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <DateCellWithException
          date={item.firstSaleDate}
          exceptionType={
            isResolved ||
            !item.exceptionTypes.includes('FIRST_SALE_DATE_BEFORE_INVENTORY')
              ? null
              : 'FIRST_SALE_DATE_BEFORE_INVENTORY'
          }
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <DateCellWithException
          date={item.scrapDate}
          exceptionType={
            isResolved ||
            !item.exceptionTypes.includes('DISPOSAL_DATE_BEFORE_INVENTORY')
              ? null
              : 'DISPOSAL_DATE_BEFORE_INVENTORY'
          }
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <DateCellWithException
          date={item.firstMovementDate}
          exceptionType={
            isResolved ||
            !item.exceptionTypes.includes('FIRST_MOVEMENT_BEFORE_INVENTORY')
              ? null
              : 'FIRST_MOVEMENT_BEFORE_INVENTORY'
          }
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <DateCellWithException
          date={item.firstAssignmentDate}
          exceptionType={
            isResolved ||
            !item.exceptionTypes.includes('FIRST_ASSIGNMENT_BEFORE_INVENTORY')
              ? null
              : 'FIRST_ASSIGNMENT_BEFORE_INVENTORY'
          }
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        {isResolved ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.5}
          >
            <CheckCircleOutlined
              fontSize="small"
              sx={{ color: 'success.main' }}
            />
            <Typography variant="body2" color="success.main">
              Resolved
            </Typography>
          </Stack>
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={() => props.data.onSelectAsset(item)}
          >
            Resolve
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * Renders a date cell with an optional exception indicator.
 * Dates are right-aligned for better scanning, and exceptions are shown
 * as a subtle icon badge with a tooltip for full details.
 */
function DateCellWithException(props: {
  date: DateTime | null;
  exceptionType: ExceptionType | null;
}) {
  const dateText = props.date
    ? props.date.toLocaleString({ dateStyle: 'medium' })
    : '—';

  if (!props.exceptionType) {
    return (
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {dateText}
      </Typography>
    );
  }

  const config = getExceptionConfig(props.exceptionType);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.75,
      }}
    >
      <Tooltip title={config.label} arrow placement="top">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap={0.75}
          sx={{
            cursor: 'help',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'error.main',
            }}
          >
            {config.icon}
          </Box>
          <Typography
            variant="subtitle2"
            color="error.dark"
            sx={{ textAlign: 'right' }}
          >
            {dateText}
          </Typography>
        </Stack>
      </Tooltip>
    </Box>
  );
}

export function DateInconsistenciesTable(props: Props) {
  const [selectedAsset, setSelectedAsset] =
    useState<DateInconsistencyItem | null>(null);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [resolvedAssetIds, setResolvedAssetIds] = useState<Set<string>>(
    new Set()
  );

  // Memoized callback to handle asset selection for the resolve dialog
  const handleSelectAsset = useCallback((item: DateInconsistencyItem) => {
    setSelectedAsset(item);
    setIsResolveModalOpen(true);
  }, []);

  // Handler for when an asset's exceptions are resolved
  // Adds the asset ID to the resolved set for optimistic UI update
  const handleAssetResolved = useCallback((assetId: string) => {
    setResolvedAssetIds((prev) => new Set(prev).add(assetId));
  }, []);

  // Calculate the height for the virtualized list
  const listHeight = Math.min(
    props.assets.length * ROW_HEIGHT,
    MAX_TABLE_HEIGHT
  );

  if (props.assets.length === 0) {
    return (
      <>
        <EmptyState
          title="No date inconsistencies found"
          message="All assets have consistent date information"
        />
        {/* Keep dialog mounted so it can show resolved state when user returns */}
        <AssetExceptionResolutionDialog
          open={isResolveModalOpen}
          onClose={() => setIsResolveModalOpen(false)}
          onExited={() => setSelectedAsset(null)}
          onResolved={handleAssetResolved}
          asset={selectedAsset?.asset}
          exceptionType="date"
          exceptions={selectedAsset?.exceptionTypes}
          dateData={
            selectedAsset
              ? {
                  inventoryDate: selectedAsset.inventoryDate,
                  inServiceDate: selectedAsset.inServiceDate,
                  scrapDate: selectedAsset.scrapDate,
                  firstSaleDate: selectedAsset.firstSaleDate,
                  firstAssignmentDate: selectedAsset.firstAssignmentDate,
                  firstMovementDate: selectedAsset.firstMovementDate,
                }
              : undefined
          }
        />
      </>
    );
  }

  return (
    <Paper variant="outlined">
      {/* Header row using CSS grid to match virtualized rows */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns:
            'minmax(150px, 1.5fr) repeat(6, minmax(90px, 1fr)) minmax(120px, 0.5fr)',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.100',
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="overline">Serial</Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Inventory
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          In service
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Sale
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Scrap
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Movement
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Assignment
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Actions
        </Typography>
      </Box>

      {/* Virtualized table body */}
      <FixedSizeList
        height={listHeight}
        width="100%"
        itemSize={ROW_HEIGHT}
        itemCount={props.assets.length}
        overscanCount={OVERSCAN_COUNT}
        itemData={{
          assets: props.assets,
          onSelectAsset: handleSelectAsset,
          resolvedAssetIds,
        }}
      >
        {VirtualizedRow}
      </FixedSizeList>

      <AssetExceptionResolutionDialog
        open={isResolveModalOpen}
        onClose={() => setIsResolveModalOpen(false)}
        onExited={() => setSelectedAsset(null)}
        onResolved={handleAssetResolved}
        asset={selectedAsset?.asset}
        exceptionType="date"
        exceptions={selectedAsset?.exceptionTypes}
        dateData={
          selectedAsset
            ? {
                inventoryDate: selectedAsset.inventoryDate,
                inServiceDate: selectedAsset.inServiceDate,
                scrapDate: selectedAsset.scrapDate,
                firstSaleDate: selectedAsset.firstSaleDate,
                firstAssignmentDate: selectedAsset.firstAssignmentDate,
                firstMovementDate: selectedAsset.firstMovementDate,
              }
            : undefined
        }
      />
    </Paper>
  );
}
